import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
} from "react"
import { createPortal } from "react-dom"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { VariantReader } from "aldoo-ra/VariantReader"

const PromptDialog = forwardRef((props, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  const [state, setState] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
    onCancel: () => {},
    onClose: () => {},
    showYesBtn: true,
    showNoBtn: true,
    yesLabel: "",
    noLabel: "",
    variant: "default",
  })

  // Show the dialog
  const show = ({
    title,
    message,
    onConfirm,
    onCancel,
    onClose,
    showYesBtn = true,
    showNoBtn = true,
    yesLabel,
    noLabel,
    confirmText,
    cancelText,
    confirmVariant, // Accept but ignore for now
    variant = "default",
  }) => {
    setIsOpen(true)
    setState({
      title,
      message,
      onConfirm,
      onCancel,
      onClose,
      showYesBtn,
      showNoBtn,
      yesLabel: confirmText || yesLabel,
      noLabel: cancelText || noLabel,
      variant,
    })
  }

  const {
    title,
    message,
    onConfirm,
    onCancel,
    onClose,
    showYesBtn,
    showNoBtn,
    yesLabel,
    noLabel,
    variant,
  } = state

  const styles = VariantReader.useVariant({
    componentName: "PromptDialog",
    variant,
  })

  // Export the show function
  useImperativeHandle(ref, () => ({
    show,
  }))

  useEffect(() => {
    if (!isOpen && onClose) {
      onClose()
    }
  }, [isOpen])

  if (!isOpen) return null
  if (!mounted || typeof document === "undefined" || !document.body) return null

  return createPortal(
    <div
      data-modal="prompt-dialog"
      className={styles.overlay}
      style={{ zIndex: 2147483647 }}
    >
      <div className={styles.container}>
        <Typography variant={styles.titleVariant} className={styles.titleClass}>
          {title}
        </Typography>

        <hr className={styles.divider} />

        {typeof message === 'string' ? (
          <Typography
            variant={styles.messageVariant}
            className={styles.messageClass}
          >
            {message}
          </Typography>
        ) : (
          <div className={`${styles.messageClass} ${styles.messageColorClass}`}>
            {message}
          </div>
        )}

        <div className={`${styles.buttonRow} ${showYesBtn && showNoBtn ? 'justify-end' : 'justify-center'}`}>
          {showNoBtn && (
            <Button
              variant={styles.cancelButtonVariant}
              className={styles.buttonClass}
              onClick={() => {
                setIsOpen(false)
                if (onCancel) onCancel()
              }}
            >
              {noLabel || `Cancel`}
            </Button>
          )}
          {showYesBtn && (
            <Button
              variant={styles.confirmButtonVariant}
              className={styles.buttonClass}
              onClick={() => {
                setIsOpen(false)
                if (onConfirm) onConfirm()
              }}
            >
              {yesLabel || `Confirm`}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
})

let promptInstance = null

export const showPromptDialog = (options) => {
  if (promptInstance === null) return
  promptInstance.show(options)
}

// Show the prompt dialog and return a promise
// that resolves when the user confirms and rejects
export const showPromptDialogAsync = (options) => {
  return new Promise((resolve, reject) => {
    showPromptDialog({
      ...options,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })
}

// PromptDialogProvider
const PromptDialogProvider = () => {
  const promptRef = useRef()

  useEffect(() => {
    promptInstance = promptRef.current
  }, [])

  return (
    <>
      <PromptDialog ref={promptRef} />
    </>
  )
}

export default PromptDialogProvider
